import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { assertPublicRateLimit } from "@/lib/rate-limit.server";
import { translateBatchCached } from "@/lib/translate-cache.server";
import { resolveServerLang } from "@/lib/resolve-lang.server";

// On-demand AI translation for user-generated marketplace content (company
// descriptions, deal listings). The platform UI is statically localized;
// content written by companies is whatever language they wrote it in — this
// bridges the gap with one click, powered by the Lovable AI gateway.

const LANG_NAMES: Record<string, string> = {
  en: "English",
  de: "German",
  es: "Spanish",
  fr: "French",
  it: "Italian",
  ru: "Russian",
  zh: "Simplified Chinese",
};

export const translatePublicText = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        texts: z.array(z.string().trim().min(1).max(4000)).min(1).max(10),
        targetLang: z.enum(["en", "de", "es", "fr", "it", "ru", "zh"]),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("translate", 15);
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Translation is not available right now.");

    const { generateText } = await import("ai");
    const { createLovableAiGatewayProvider } = await import("@/lib/ai-gateway.server");
    const gateway = createLovableAiGatewayProvider(key);
    const { text } = await generateText({
      model: gateway("google/gemini-3-flash-preview"),
      system: `You are a professional business translator. Translate every string in the JSON array the user sends into ${LANG_NAMES[data.targetLang]}. Preserve meaning, tone, formatting and line breaks. Keep brand names, product names, URLs and email addresses unchanged. If a string is already in ${LANG_NAMES[data.targetLang]}, return it unchanged. Reply with ONLY a valid JSON array of the translated strings — same order, same length, no commentary.`,
      prompt: JSON.stringify(data.texts),
      maxOutputTokens: 4000,
    });

    const match = text.match(/\[[\s\S]*\]/);
    if (!match) throw new Error("Translation failed — please try again.");
    let arr: unknown;
    try {
      arr = JSON.parse(match[0]);
    } catch {
      throw new Error("Translation failed — please try again.");
    }
    if (!Array.isArray(arr) || arr.length !== data.texts.length) {
      throw new Error("Translation failed — please try again.");
    }
    return { translations: arr.map((s) => String(s)) };
  });

// Persistent, cached translation for our own long-form public content (docs,
// blog, competitor comparisons, support articles). Unlike translatePublicText
// this is backed by translateBatchCached: translated once per language, then
// served from cache. It never throws — on any failure it returns the English
// input, so callers (including SSR loaders) can rely on always getting an
// array of the same length. targetLang is optional; when omitted it is resolved
// from the request (rt_lang cookie / Accept-Language) for SSR use.
export const translateContent = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        texts: z.array(z.string()).max(120),
        targetLang: z.enum(["en", "de", "es", "fr", "it", "ru", "zh"]).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("translate-content", 40);
    const lang = data.targetLang ?? resolveServerLang();
    const clipped = data.texts.map((t) => t.slice(0, 6000));
    const translations = await translateBatchCached(clipped, lang);
    return { lang, translations };
  });
